/**
 * British English formatting helpers.
 * Currency GBP (£), numbers with comma grouping, dates DD/MM/YYYY.
 */

/** Format a number with comma grouping (no decimals). */
export function fmtNum(n: number): string {
  return n.toLocaleString('en-GB', { maximumFractionDigits: 0 });
}

/** Format a number as GBP (£) with comma grouping, or em-dash if null/NaN. */
export function fmtGBP(n: number | null | undefined): string {
  if (n === null || n === undefined || isNaN(n)) return '—';
  return `£${fmtNum(n)}`;
}

/** Format a Date as DD/MM/YYYY. */
export function fmtDate(d: Date): string {
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

/** Format a Date as a London-time string, e.g. "04/08/2026, 10:30 am". */
export function fmtLondonTime(d: Date): string {
  return d.toLocaleString('en-GB', {
    timeZone: 'Europe/London',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
}

/**
 * Parse a slot label ("9.00 am", "10.30 am", "12.00 pm", "1.15 pm", …) into a
 * UTC Date for a given day, treating the slot time as Europe/London local time
 * (handles BST/GMT correctly via the Intl API). Used by the Book page to send
 * the correct slotStart/slotEnd to the BFF.
 *
 * @param day  a Date at midnight (the selected day)
 * @param label  a slot label like "9.00 am" or "1.15 pm"
 * @returns a UTC Date for that slot's start, or the day itself if unparseable
 */
export function slotLabelToDate(day: Date, label: string): Date {
  const match = label.match(/^(\d+)\.(\d+)\s*(am|pm)$/i);
  if (!match) return new Date(day);
  let hours = parseInt(match[1]!, 10);
  const minutes = parseInt(match[2]!, 10);
  const period = match[3]!.toLowerCase();
  if (period === 'pm' && hours !== 12) hours += 12;
  if (period === 'am' && hours === 12) hours = 0;

  // Build a London-local wall-clock string (YYYY-MM-DDTHH:MM:00) for the chosen
  // day, then convert to UTC using the Europe/London offset for that instant.
  const y = day.getFullYear();
  const m = String(day.getMonth() + 1).padStart(2, '0');
  const d = String(day.getDate()).padStart(2, '0');
  const hh = String(hours).padStart(2, '0');
  const mm = String(minutes).padStart(2, '0');
  const londonWall = `${y}-${m}-${d}T${hh}:${mm}:00`;
  // Treat as London: find the offset for that wall time by formatting a naive
  // UTC Date through Europe/London and comparing the wall-clock difference.
  const naiveUtc = new Date(`${londonWall}Z`);
  const londonOffsetMin = getLondonOffsetMinutes(naiveUtc);
  return new Date(naiveUtc.getTime() - londonOffsetMin * 60 * 1000);
}

/** Get the Europe/London UTC offset (in minutes) for a given instant. */
function getLondonOffsetMinutes(date: Date): number {
  const dtf = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/London',
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
  });
  const parts = dtf.formatToParts(date);
  const map = Object.fromEntries(parts.map((p) => [p.type, p.value])) as Record<string, string>;
  const asUtc = Date.UTC(
    Number(map.year), Number(map.month) - 1, Number(map.day),
    Number(map.hour), Number(map.minute), Number(map.second),
  );
  return Math.round((asUtc - date.getTime()) / 60000);
}